"use client";
import React, {forwardRef} from "react";



import {zodResolver} from '@hookform/resolvers/zod';
import {Controller, useForm} from 'react-hook-form';
import {z} from 'zod';

import {Form, FormControl, FormField, FormItem, FormMessage} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import {Textarea} from "@/components/ui/textarea";
import {Checkbox} from "@/components/ui/checkbox";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {provinces} from "@/components/FormLead";
import {useRouter} from "next/navigation";
import {getLeadSourceValue} from "@/utils/leadSource";

export interface FromNoleggioProps {
    articolo: string;
    marca: string;
    modello: string;
    title?: string;
    durata: string;
    km: string;
    anticipo: string;
}


const formSchema = z.object({
    nome: z.string().min(1, {message: 'Il nome è obbligatorio.'}),
    cognome: z.string().min(1, {message: 'Il cognome è obbligatorio.'}),
    email: z.string().email({message: 'Inserisci un indirizzo email valido.'}).min(1, {message: 'L\'email è obbligatoria.'}),
    telefono: z.string().min(10, {message: 'Il numero di telefono deve avere almeno 10 cifre.'}).min(1, {message: 'Il telefono è obbligatorio.'}),
    descrizione: z.string().optional(),
    provincia: z.string().min(1, {message: 'La provincia è obbligatoria.'}),
    terms: z.boolean().default(false).refine(val => val, {message: 'Devi accettare i termini e le condizioni.'})
});

const FromNoleggio = forwardRef<HTMLDivElement, FromNoleggioProps>(({
                                                                        articolo,
                                                                        marca,
                                                                        modello,
                                                                        durata,
                                                                        km,
                                                                        anticipo,
                                                                        title = 'Compila i dati per stampare il preventivo' }, ref) => {

    const router = useRouter();

    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onTouched',
        defaultValues: {
            nome: '',
            cognome: '',
            email: '',
            telefono: '',
            provincia: '',
            descrizione: '',
            terms: false
        }
    });

    const onSubmit = async (data: any) => {
        console.log({
            ...data,
            articolo,
            marca,
            modello
        });

        // Deconstruisco i dati dal form
        const {nome, email, cognome, telefono, provincia, descrizione} = data;

        // Preparo i dati da inviare nel body della richiesta
        const body = {
            origine: 'preventivo ' + articolo,
            argomento: 'LDNL',
            nome: nome ?? '',
            cognome: cognome ?? '',
            telefono: telefono ?? '',
            email: email ?? '',
            provincia: provincia ?? '',
            citta: provincia ? provinces.find((p) => p.code === provincia)?.name : '',
            motivazione: descrizione ?? marca + ' ' + modello + ' ' + articolo,
            fonte: getLeadSourceValue(),
            articolo: articolo,
            marca: marca,
            modello: modello,
            durata: durata,
            km: km,
            anticipo: anticipo,
        };

        // Invio la richiesta POST all'API
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/wsdl/lead`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });

        // Controllo la risposta
        const result = await response.json();

        console.log('Risultato della richiesta:', result);

        if (response.ok && result.status === 'success') {
            // Reindirizzo alla pagina thank-you con i parametri dinamici
            // pulisco i dati dal form

            if (result.pdf) {
                // Creare un link temporaneo per il download
                const link = document.createElement('a');
                link.href = result.pdf;
                link.target = '_blank';
                link.download = 'preventivo.pdf'; // Imposta il nome del file se necessario
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }

            form.reset();
            const descrizione = title + ' ' + marca + ' ' + modello + ' ' + articolo;
            router.push(`/thank-you-page/noleggio?descrizione=${encodeURIComponent(descrizione)}&email=${encodeURIComponent(email)}&nome=${encodeURIComponent(nome)}&telefono=${encodeURIComponent(telefono)}`);

        } else {
            // Gestisci l'errore, ad esempio mostrando un messaggio di errore all'utente
            console.error('Errore durante l\'invio dei dati:', result);
        }

        // Reindirizzo alla pagina thank-you con i parametri dinamici

    };

    return (
        <section ref={ref}  id="form-noleggio" className="my-24">
            <h5 className="text-4xl font-normal">{title}</h5>
            <p className="text-xl font-normal mb-8">senza impegno</p>

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-4">
                        <FormField control={form.control} name="nome" render={({field, fieldState}) => (
                            <FormItem>
                                <FormControl>
                                    <Input {...field} className="border-primary" placeholder="Nome"/>
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}/>
                        <FormField control={form.control} name="cognome" render={({field, fieldState}) => (
                            <FormItem>
                                <FormControl>
                                    <Input {...field} className="border-primary" placeholder="Cognome"/>
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}/>
                    </div>
                    <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-4">
                        <FormField control={form.control} name="email" render={({field, fieldState}) => (
                            <FormItem>
                                <FormControl>
                                    <Input {...field} className="border-primary" placeholder="Indirizzo Email"/>
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}/>
                        <FormField control={form.control} name="telefono" render={({field, fieldState}) => (
                            <FormItem>
                                <FormControl>
                                    <Input {...field} className="border-primary" placeholder="Numero di telefono"/>
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}/>
                    </div>
                    <FormField control={form.control} name="descrizione" render={({field, fieldState}) => (
                        <FormItem>
                            <FormControl>
                                <Textarea {...field} className="border-primary"
                                          placeholder="Descrivi la tua richiesta"/>
                            </FormControl>
                            <FormMessage>{fieldState.error?.message}</FormMessage>
                        </FormItem>
                    )}/>
                    <FormField control={form.control} name="provincia"
                               render={({field, fieldState}) => (
                                   <FormItem>
                                       <FormControl>
                                           <Controller
                                               control={form.control}
                                               name="provincia"
                                               render={({field}) => (
                                                   <Select {...field}
                                                           onValueChange={(value) => field.onChange(value)}
                                                           value={field.value}>
                                                       <SelectTrigger
                                                           className="border-primary">
                                                           <SelectValue
                                                               placeholder="Seleziona una provincia"/>
                                                       </SelectTrigger>
                                                       <SelectContent>
                                                           {provinces.map((province) => (
                                                               <SelectItem
                                                                   key={province.code}
                                                                   value={province.code}>
                                                                   {province.name}
                                                               </SelectItem>
                                                           ))}
                                                       </SelectContent>
                                                   </Select>
                                               )}
                                           />
                                       </FormControl>
                                       <FormMessage>{fieldState.error?.message}</FormMessage>
                                   </FormItem>
                               )}/>
                    <div className="flex items-center">
                        <FormField control={form.control} name="terms" render={({field, fieldState}) => (
                            <FormItem>
                                <FormControl>
                                    <Checkbox checked={field.value} className="text-white"
                                              onCheckedChange={field.onChange}/>
                                </FormControl>
                            </FormItem>
                        )}/>
                        <label htmlFor="terms" className="ml-2 cursor-pointer">
                            Accetto i <a href="#" className="text-primary underline">termini e
                            condizioni</a>.
                        </label>
                        {form.formState.errors.terms && (
                            <div className="text-red-500 text-sm mt-1">
                                {form.formState.errors.terms.message}
                            </div>
                        )}
                    </div>
                    <button
                        className="bg-gradient-custom px-6 py-2 min-w-[200px] rounded-lg text-white text-center font-bold">Invia
                    </button>
                </form>
            </Form>
        </section>
    )
});

export default FromNoleggio;
